import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { QRCodeSVG } from 'qrcode.react';
import {
  getAdminStats, getAdminUsers, createCompUser, updateAdminUser, revokeAdminUser,
  getPromoCodes, createPromoCode, updatePromoCode, deletePromoCode,
  getManualFacilities, createManualFacility, deleteManualFacility,
  getPendingLinks, approveLink, denyLink,
  getFacility, getAdminNotes,
} from '../api';
import UploadZone, { PDF_ACCEPT } from '../components/UploadZone';
import SyncSummaryModal from '../components/SyncSummaryModal';
import FacilitySearchSelect from '../components/FacilitySearchSelect';
import { uploadExcel, uploadHousing, uploadSoberLiving, uploadWebsites, uploadMoud, updateBanner, getBanner } from '../api';

// ─── Shared helpers ──────────────────────────────────────────────────────────

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'comp_individual', label: 'Comp Individual' },
  { value: 'comp_org',        label: 'Comp Organization' },
];

const WA_COUNTIES = [
  'Adams','Asotin','Benton','Chelan','Clallam','Clark','Columbia','Cowlitz',
  'Douglas','Ferry','Franklin','Garfield','Grant','Grays Harbor','Island',
  'Jefferson','King','Kitsap','Kittitas','Klickitat','Lewis','Lincoln','Mason',
  'Okanogan','Pacific','Pend Oreille','Pierce','San Juan','Skagit','Skamania',
  'Snohomish','Spokane','Stevens','Thurston','Wahkiakum','Walla Walla',
  'Whatcom','Whitman','Yakima',
];

const ACCOUNT_LABELS = {
  guest: 'Guest',
  comp_individual: 'Comp Indv', comp_org: 'Comp Org',
  subscriber_individual: 'Sub Indv', subscriber_org: 'Sub Org', admin: 'Admin',
};

const ACCOUNT_COLORS = {
  guest: 'bg-gray-100 text-gray-600',
  comp_individual: 'bg-purple-100 text-purple-700',
  comp_org: 'bg-purple-100 text-purple-700',
  subscriber_individual: 'bg-green-100 text-green-700',
  subscriber_org: 'bg-green-100 text-green-700',
  admin: 'bg-blue-100 text-blue-700',
};

function Badge({ type }) {
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ACCOUNT_COLORS[type] || ACCOUNT_COLORS.guest}`}>
      {ACCOUNT_LABELS[type] || type}
    </span>
  );
}

function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {label}
    </button>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminStats()
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const StatCard = ({ label, value, sub, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">{label}</p>
      <p className={`text-3xl font-bold ${color || 'text-gray-900'}`}>{loading ? '—' : value ?? '—'}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Individual Subscribers" value={stats?.subscriber_individual}  color="text-green-600" sub="$9.99/mo each" />
        <StatCard label="Org Subscribers"         value={stats?.subscriber_org}         color="text-green-600" sub="$25/mo each" />
        <StatCard label="Active Comp Accounts"    value={stats?.comp_active}     color="text-purple-600" />
        <StatCard label="Expired Comp (convert)"  value={stats?.comp_expired}    color="text-orange-500" sub="Conversion targets" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Est. Monthly Revenue"
          value={stats ? `$${((stats.subscriber_individual || 0) * 19 + (stats.subscriber_org || 0) * 25).toLocaleString()}` : null}
          color="text-blue-700" />
        <StatCard label="Total Users" value={stats?.total_users} />
        <StatCard label="Total Facilities" value={stats?.total_facilities} />
      </div>
    </div>
  );
}

// ─── Users tab ────────────────────────────────────────────────────────────────

function UserEditModal({ user, onSave, onClose }) {
  const [form, setForm] = useState({
    account_type:       user?.account_type || 'comp_individual',
    comp_expiration_on: user?.comp_expiration_on || false,
    comp_expires_at:    user?.comp_expires_at ? user.comp_expires_at.slice(0, 10) : '',
    comp_note:          user?.comp_note || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // Facility linking state
  const [linkedFacilityId, setLinkedFacilityId]     = useState(user?.linked_facility_id || null);
  const [currentFacilityName, setCurrentFacilityName] = useState('');
  const [selectedFacility, setSelectedFacility]     = useState(null);
  const [facilityChanged, setFacilityChanged]       = useState(false);

  // Fetch the name of the currently linked facility on open
  useEffect(() => {
    if (user?.linked_facility_id) {
      getFacility(user.linked_facility_id)
        .then((f) => setCurrentFacilityName(f?.agency_name || `Facility #${user.linked_facility_id}`))
        .catch(() => setCurrentFacilityName(`Facility #${user.linked_facility_id}`));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const handleSelectFacility = (f) => {
    setSelectedFacility(f);
    setLinkedFacilityId(f?.id || null);
    setFacilityChanged(true);
  };

  const handleClearLink = () => {
    setSelectedFacility(null);
    setLinkedFacilityId(null);
    setFacilityChanged(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        account_type:       form.account_type,
        comp_expiration_on: form.comp_expiration_on,
        comp_expires_at:    form.comp_expiration_on && form.comp_expires_at ? form.comp_expires_at : null,
        comp_note:          form.comp_note || null,
      };
      if (facilityChanged) {
        payload.linked_facility_id = linkedFacilityId || null;
      }
      await onSave(payload);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const isOrgType = ['comp_org', 'subscriber_org'].includes(form.account_type);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-7 overflow-y-auto max-h-[90vh]">
        <h3 className="text-base font-bold text-gray-900 mb-1">Edit Account</h3>
        <p className="text-sm text-gray-500 mb-5">{user?.email}</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Account Type</label>
            <select value={form.account_type} onChange={set('account_type')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400">
              {ACCOUNT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              <option value="guest">Guest (revoke comp)</option>
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.comp_expiration_on} onChange={set('comp_expiration_on')}
              className="w-4 h-4 text-blue-600 rounded" />
            <span className="text-sm text-gray-700">Set expiration date</span>
          </label>

          {form.comp_expiration_on && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Expiration Date</label>
              <input type="date" value={form.comp_expires_at} onChange={set('comp_expires_at')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Admin Note <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea value={form.comp_note} onChange={set('comp_note')} rows={2}
              placeholder="e.g. Conference promo — check back in 6 months"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>

          {/* Facility linking — org accounts only */}
          {isOrgType && (
            <div className="border-t border-gray-100 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Linked Facility
              </label>

              {/* Show current link (if any and not changed yet) */}
              {!facilityChanged && linkedFacilityId && (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2">
                  <p className="text-sm text-green-800 font-medium truncate">
                    {currentFacilityName || `Facility #${linkedFacilityId}`}
                  </p>
                  <button onClick={handleClearLink}
                    className="ml-2 flex-shrink-0 text-xs text-red-500 hover:text-red-700 font-medium">
                    Remove
                  </button>
                </div>
              )}

              {/* Show cleared state */}
              {facilityChanged && !linkedFacilityId && (
                <p className="text-xs text-orange-600 mb-2">
                  Link will be removed on save.
                </p>
              )}

              {/* Search for a new facility */}
              <FacilitySearchSelect
                selected={selectedFacility}
                onSelect={handleSelectFacility}
                placeholder="Search to link or change facility…"
              />
              <p className="text-xs text-gray-400 mt-1.5">
                Setting a facility link skips the approval step.
              </p>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button onClick={handleSave} disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button onClick={onClose}
              className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', organization_name: '',
    role_title: '', primary_county: '',
    account_type: 'comp_individual',
    comp_expiration_on: false, comp_expires_at: '', comp_note: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const canSave = form.first_name && form.last_name && form.email && form.password && form.organization_name;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        comp_expires_at: form.comp_expiration_on && form.comp_expires_at ? form.comp_expires_at : null,
      });
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not create account.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-7 overflow-y-auto max-h-screen">
        <h3 className="text-base font-bold text-gray-900 mb-5">Create Complimentary Account</h3>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[['first_name','First name'],['last_name','Last name']].map(([k,l]) => (
              <div key={k}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{l} *</label>
                <input type="text" value={form[k]} onChange={set(k)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
            ))}
          </div>

          {[['email','Email *','email'],['organization_name','Organization *','text']].map(([k,l,t]) => (
            <div key={k}>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{l}</label>
              <input type={t} value={form[k]} onChange={set(k)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          ))}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Password *</label>
            <input type="password" value={form.password} onChange={set('password')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
              <input type="text" value={form.role_title} onChange={set('role_title')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">County</label>
              <select value={form.primary_county} onChange={set('primary_county')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400">
                <option value="">Select…</option>
                {WA_COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Account Type</label>
            <select value={form.account_type} onChange={set('account_type')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400">
              {ACCOUNT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.comp_expiration_on} onChange={set('comp_expiration_on')}
              className="w-4 h-4 text-blue-600 rounded" />
            <span className="text-sm text-gray-700">Set expiration date</span>
          </label>

          {form.comp_expiration_on && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Expiration Date</label>
              <input type="date" value={form.comp_expires_at} onChange={set('comp_expires_at')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Admin Note</label>
            <textarea value={form.comp_note} onChange={set('comp_note')} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button onClick={handleSave} disabled={!canSave || saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50">
              {saving ? 'Creating…' : 'Create account'}
            </button>
            <button onClick={onClose}
              className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = () => {
    setLoading(true);
    getAdminUsers()
      .then(setUsers)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      u.email?.toLowerCase().includes(q) ||
      u.organization_name?.toLowerCase().includes(q) ||
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q);
    const matchType = !typeFilter || u.account_type === typeFilter;
    return matchSearch && matchType;
  });

  const handleSaveEdit = async (data) => {
    await updateAdminUser(editingUser.id, data);
    setEditingUser(null);
    load();
  };

  const handleCreate = async (data) => {
    await createCompUser(data);
    setShowCreate(false);
    load();
  };

  const handleRevoke = async (userId) => {
    await revokeAdminUser(userId);
    setConfirmRevoke(null);
    load();
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, org…"
          className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">All types</option>
          {Object.entries(ACCOUNT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={() => setShowCreate(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
          + Create comp account
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name / Org', 'Email', 'Type', 'Expiration', 'Pwd Changed', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">No users match your filter</td></tr>
              ) : filtered.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</p>
                    <p className="text-xs text-gray-400">{u.organization_name}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{u.email}</td>
                  <td className="px-4 py-3"><Badge type={u.account_type} /></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {u.comp_expiration_on && u.comp_expires_at
                      ? new Date(u.comp_expires_at).toLocaleDateString()
                      : u.account_type?.startsWith('comp') ? 'No expiry' : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {u.must_change_password
                      ? <span className="text-amber-600 font-medium">Not yet set</span>
                      : u.password_changed_at
                        ? <span className="text-gray-500">{new Date(u.password_changed_at).toLocaleDateString()}</span>
                        : <span className="text-gray-400">—</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setEditingUser(u)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium">Edit</button>
                      {confirmRevoke === u.id ? (
                        <>
                          <button onClick={() => handleRevoke(u.id)}
                            className="text-xs text-red-600 hover:text-red-800 font-medium">Confirm</button>
                          <button onClick={() => setConfirmRevoke(null)}
                            className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmRevoke(u.id)}
                          className="text-xs text-gray-400 hover:text-red-600">Revoke</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingUser && (
        <UserEditModal user={editingUser} onSave={handleSaveEdit} onClose={() => setEditingUser(null)} />
      )}
      {showCreate && (
        <CreateUserModal onSave={handleCreate} onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

// ─── Promo Codes tab ──────────────────────────────────────────────────────────

function PromoTab() {
  const [promos, setPromos]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [qrPromo, setQrPromo]       = useState(null);
  const [editingPromo, setEditingPromo] = useState(null); // { id, max_uses }
  const [editMaxUses, setEditMaxUses]   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({
    code: '', description: '', account_type: 'comp_individual',
    duration_days: 30, max_uses: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const qrRef = useRef(null);

  const load = () => {
    setLoading(true);
    getPromoCodes().then(setPromos).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const created = await createPromoCode({
        ...form,
        duration_days: Number(form.duration_days),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
      });
      setPromos((prev) => [created, ...prev]);
      setForm({ code: '', description: '', account_type: 'comp_individual', duration_days: 30, max_uses: '' });
      setShowForm(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not create promo code.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async () => {
    const newMax = editMaxUses === '' ? null : Number(editMaxUses);
    try {
      const updated = await updatePromoCode(editingPromo.id, { max_uses: newMax });
      setPromos((prev) => prev.map((p) => p.id === updated.id ? updated : p));
      setEditingPromo(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not update promo code.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePromoCode(id);
      setPromos((prev) => prev.filter((p) => p.id !== id));
      setConfirmDelete(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not delete promo code.');
    }
  };

  const downloadQr = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const serialized = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([serialized], { type: 'image/svg+xml;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `promo-${qrPromo.code}.svg`; a.click();
    URL.revokeObjectURL(url);
  };

  // QR code URL — placeholder domain; replace with actual domain at deploy time
  const qrUrl = (code) => `${window.location.origin}/trial?code=${code}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{promos.length} promo codes</p>
        <button onClick={() => setShowForm((v) => !v)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
          {showForm ? 'Cancel' : '+ New promo code'}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Code *</label>
            <input type="text" value={form.code} onChange={set('code')} placeholder="e.g. CONF2026"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 uppercase" />
            <p className="text-xs text-gray-400 mt-1">The person signing up will choose Individual or Organization when they redeem this code.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <input type="text" value={form.description} onChange={set('description')} placeholder="e.g. Spring conference handout"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Duration (days)</label>
              <input type="number" value={form.duration_days} onChange={set('duration_days')} min={1}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max uses (blank = unlimited)</label>
              <input type="number" value={form.max_uses} onChange={set('max_uses')} min={1} placeholder="Unlimited"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button onClick={handleCreate} disabled={!form.code || saving}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50">
            {saving ? 'Creating…' : 'Create promo code'}
          </button>
        </div>
      )}

      {/* Promo list */}
      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Code', 'Type', 'Duration', 'Uses', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {promos.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">No promo codes yet</td></tr>
              ) : promos.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono font-semibold text-gray-900">{p.code}</td>
                  <td className="px-4 py-3"><Badge type={p.account_type} /></td>
                  <td className="px-4 py-3 text-gray-600">{p.duration_days}d</td>
                  <td className="px-4 py-3 text-gray-600">
                    {p.use_count}{p.max_uses ? `/${p.max_uses}` : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setQrPromo(p)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                        QR code
                      </button>
                      <button onClick={() => { setEditingPromo(p); setEditMaxUses(p.max_uses ?? ''); }}
                        className="text-xs text-gray-500 hover:text-blue-600 font-medium">
                        Edit limit
                      </button>
                      {confirmDelete === p.id ? (
                        <>
                          <button onClick={() => handleDelete(p.id)}
                            className="text-xs text-red-600 hover:text-red-800 font-medium">Confirm</button>
                          <button onClick={() => setConfirmDelete(null)}
                            className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmDelete(p.id)}
                          className="text-xs text-gray-400 hover:text-red-600">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit limit modal */}
      {editingPromo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl p-7 max-w-xs w-full mx-4">
            <h3 className="text-base font-bold text-gray-900 mb-1">Edit Max Uses</h3>
            <p className="text-sm text-gray-500 mb-4 font-mono">{editingPromo.code}</p>
            <label className="block text-xs font-medium text-gray-600 mb-1">Max uses (blank = unlimited)</label>
            <input
              type="number" min={1} value={editMaxUses}
              onChange={(e) => setEditMaxUses(e.target.value)}
              placeholder="Unlimited"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 mb-4"
            />
            <div className="flex gap-3">
              <button onClick={handleEditSave}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
                Save
              </button>
              <button onClick={() => setEditingPromo(null)}
                className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR modal */}
      {qrPromo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center gap-4 max-w-xs w-full mx-4">
            <h3 className="text-base font-bold text-gray-900">{qrPromo.code}</h3>
            <p className="text-xs text-gray-500 text-center">{qrPromo.description}</p>
            <div ref={qrRef} className="p-3 border border-gray-200 rounded-xl">
              <QRCodeSVG value={qrUrl(qrPromo.code)} size={180} />
            </div>
            <p className="text-xs text-gray-400 text-center break-all">{qrUrl(qrPromo.code)}</p>
            <div className="flex gap-3 w-full">
              <button onClick={downloadQr}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
                Download SVG
              </button>
              <button onClick={() => setQrPromo(null)}
                className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Facilities tab ───────────────────────────────────────────────────────────

const EMPTY_FACILITY = {
  agency_name: '', credential_number: '', status: 'Active',
  physical_street: '', physical_city: '', physical_county: '', physical_zip: '', phone_number: '',
  serves_mh: false, serves_sud: false, has_inpatient: false, has_outpatient: false,
  has_detox: false, has_psych_meds: false, has_mat: false, serves_adults: false, serves_youth: false,
};

const SERVICE_FLAGS = [
  { key: 'serves_mh',     label: 'Mental Health' },
  { key: 'serves_sud',    label: 'SUD' },
  { key: 'has_inpatient', label: 'Inpatient' },
  { key: 'has_outpatient',label: 'Outpatient' },
  { key: 'has_detox',     label: 'Detox' },
  { key: 'has_psych_meds',label: 'Psych Meds' },
  { key: 'has_mat',       label: 'MAT' },
  { key: 'serves_adults', label: 'Adults' },
  { key: 'serves_youth',  label: 'Youth' },
];

function AddFacilityModal({ onSave, onClose }) {
  const [form, setForm] = useState(EMPTY_FACILITY);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const setField = (k) => (e) => setForm((f) => ({
    ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const canSave = form.agency_name.trim() && form.credential_number.trim() && form.physical_county;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save facility.');
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-7 overflow-y-auto max-h-[90vh]">
        <h3 className="text-base font-bold text-gray-900 mb-1">Add Facility</h3>
        <p className="text-xs text-gray-400 mb-5">Manually added facilities are preserved when you re-upload the Greenbook.</p>

        <div className="space-y-4">
          {/* Agency name + credential number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Agency Name *</label>
            <input type="text" value={form.agency_name} onChange={setField('agency_name')} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Credential Number *</label>
              <input type="text" value={form.credential_number} onChange={setField('credential_number')}
                placeholder="e.g. MANUAL.001" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
              <select value={form.status} onChange={setField('status')} className={`${inputCls} bg-white`}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Street</label>
            <input type="text" value={form.physical_street} onChange={setField('physical_street')} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">City</label>
              <input type="text" value={form.physical_city} onChange={setField('physical_city')} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">County *</label>
              <select value={form.physical_county} onChange={setField('physical_county')} className={`${inputCls} bg-white`}>
                <option value="">Select…</option>
                {WA_COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Zip</label>
              <input type="text" value={form.physical_zip} onChange={setField('physical_zip')} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
              <input type="text" value={form.phone_number} onChange={setField('phone_number')} className={inputCls} />
            </div>
          </div>

          {/* Service flags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Services</label>
            <div className="grid grid-cols-3 gap-y-2 gap-x-3">
              {SERVICE_FLAGS.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input type="checkbox" checked={!!form[key]} onChange={setField(key)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400" />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button onClick={handleSave} disabled={!canSave || saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50">
              {saving ? 'Saving…' : 'Add facility'}
            </button>
            <button onClick={onClose}
              className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FacilitiesTab() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    setLoading(true);
    getManualFacilities().then(setFacilities).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleAdd = async (data) => {
    const created = await createManualFacility(data);
    setFacilities((prev) => [...prev, created].sort((a, b) => a.agency_name.localeCompare(b.agency_name)));
    setShowAdd(false);
  };

  const handleDelete = async (id) => {
    await deleteManualFacility(id);
    setFacilities((prev) => prev.filter((f) => f.id !== id));
    setConfirmDelete(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{facilities.length} manually added {facilities.length === 1 ? 'facility' : 'facilities'}</p>
        <button onClick={() => setShowAdd(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
          + Add facility
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
      ) : facilities.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-6 py-12 text-center">
          <p className="text-gray-400 text-sm">No manually added facilities yet.</p>
          <p className="text-gray-400 text-xs mt-1">Agencies added here won't be affected by Greenbook uploads.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Agency', 'County / City', 'Phone', 'Services', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {facilities.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{f.agency_name}</p>
                    <p className="text-xs text-gray-400 font-mono">{f.credential_number}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <p>{f.physical_county}</p>
                    {f.physical_city && <p className="text-xs text-gray-400">{f.physical_city}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{f.phone_number || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {SERVICE_FLAGS.map(({ key, label }) => f[key] && (
                        <span key={key} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                          {label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {confirmDelete === f.id ? (
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleDelete(f.id)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium">Confirm</button>
                        <button onClick={() => setConfirmDelete(null)}
                          className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDelete(f.id)}
                        className="text-xs text-gray-400 hover:text-red-600">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddFacilityModal onSave={handleAdd} onClose={() => setShowAdd(false)} />}
    </div>
  );
}

// ─── Upload tab ───────────────────────────────────────────────────────────────

function UploadSection({ title, description, uploadFn, passwordRequired = false }) {
  const [password, setPassword] = useState('');
  const [file, setFile]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [summary, setSummary]   = useState(null);
  const [error, setError]       = useState('');

  const canSubmit = file && (!passwordRequired || password.trim()) && !loading;

  const handleUpload = async () => {
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    const formData = new FormData();
    if (passwordRequired) formData.append('password', password);
    formData.append('file', file);
    try {
      const result = await uploadFn(formData);
      setSummary(result);
      setFile(null);
      setPassword('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 mb-4">{description}</p>
      {passwordRequired && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter upload password"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
            disabled={loading}
          />
        </div>
      )}
      <UploadZone file={file} onFileAccepted={setFile} />
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      <button
        onClick={handleUpload}
        disabled={!canSubmit}
        className={`mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-colors ${
          canSubmit ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        {loading ? (
          <>
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            Uploading & syncing…
          </>
        ) : 'Upload & Sync'}
      </button>
      <SyncSummaryModal summary={summary} onClose={() => setSummary(null)} />
    </div>
  );
}

function BannerEditor() {
  const [text, setText]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    getBanner().then((d) => setText(d.text || '')).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await updateBanner(text);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-1">Update Banner</h3>
      <p className="text-sm text-gray-500 mb-3">
        Scrolling announcement bar shown at the top of every page. Leave blank to hide it.
      </p>
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setSaved(false); }}
        rows={3}
        placeholder='e.g. "Housing tab added • Notes now available for all users • FAQ updated"'
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
      />
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      <div className="flex items-center gap-3 mt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            saved ? 'bg-green-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save Banner'}
        </button>
        {text && (
          <button
            onClick={() => { setText(''); setSaved(false); }}
            className="text-sm text-gray-400 hover:text-red-500"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

function UploadTab() {
  return (
    <div className="max-w-lg space-y-8">
      <UploadSection
        title="Greenbook — Treatment Facilities"
        description="Upload the monthly Greenbook .xlsx file to sync facility data. Upload password required."
        uploadFn={uploadExcel}
        passwordRequired
      />
      <hr className="border-gray-200" />
      <UploadSection
        title="Oxford Houses — Housing Directory"
        description="Upload the meeting_directory.xlsx file to sync housing data. No password required (admin login is sufficient)."
        uploadFn={uploadHousing}
        passwordRequired={false}
      />
      <hr className="border-gray-200" />
      <UploadSection
        title="Sober Living — Housing Directory"
        description="Upload a sober living .xlsx file to sync sober living home data. No password required (admin login is sufficient)."
        uploadFn={uploadSoberLiving}
        passwordRequired={false}
      />
      <hr className="border-gray-200" />
      <MoudUploadSection />
      <hr className="border-gray-200" />
      <WebsiteUploadSection />
      <hr className="border-gray-200" />
      <BannerEditor />
    </div>
  );
}

// ─── MOUD (Help Line PDF) upload section ─────────────────────────────────────

const MOUD_LISTS = [
  { value: 'buprenorphine', label: 'Buprenorphine Based Programs' },
  { value: 'methadone',     label: 'Methadone Based Programs' },
  { value: 'naltrexone',    label: 'Naltrexone Based Programs' },
];

function MoudUploadSection() {
  const [file,       setFile]       = useState(null);
  const [medication, setMedication] = useState('buprenorphine');
  const [loading,    setLoading]    = useState(false);
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState('');

  const handleUpload = async () => {
    if (!file) return;
    setError('');
    setResult(null);
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('medication', medication);
    try {
      const r = await uploadMoud(formData);
      setResult(r);
      setFile(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-1">MOUD Programs — Recovery Help Line PDF</h3>
      <p className="text-sm text-gray-500 mb-4">
        On search.warecoveryhelpline.org, open a medication list (Buprenorphine, Methadone, or Naltrexone Based Programs),
        print it to PDF, and upload it here. Each list syncs separately; a program on more than one list keeps all its medications.
      </p>
      <select
        value={medication}
        onChange={(e) => setMedication(e.target.value)}
        className="mb-3 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        {MOUD_LISTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <UploadZone file={file} onFileAccepted={setFile} accept={PDF_ACCEPT} hint="Drag & drop the Help Line PDF printout here" />
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      <button
        onClick={handleUpload}
        disabled={!file || loading}
        className={`mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-colors ${
          file && !loading ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        {loading ? (
          <>
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            Parsing & syncing…
          </>
        ) : 'Upload & Sync'}
      </button>

      {result && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-green-800 mb-1">
            {result.medication} list synced — {result.programs} programs from {result.pages} pages
          </p>
          <p className="text-sm text-green-700">
            {result.added} added · {result.updated} updated · {result.unlisted} no longer on this list
            {result.removed > 0 && ` (${result.removed} removed entirely)`}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Website upload section ───────────────────────────────────────────────────

function WebsiteUploadSection() {
  const [file,    setFile]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');

  const handleUpload = async () => {
    if (!file) return;
    setError('');
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await uploadWebsites(formData);
      setResult(r);
      setFile(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-1">Agency Websites</h3>
      <p className="text-sm text-gray-500 mb-4">
        Upload a spreadsheet to add/update website URLs on existing facilities.
        Two columns required: <strong>Agency Name</strong> and <strong>Website URL</strong>.
        Row 1 is treated as a header and skipped. Will never delete or overwrite other data.
      </p>
      <UploadZone file={file} onFileAccepted={setFile} />
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      <button
        onClick={handleUpload}
        disabled={!file || loading}
        className={`mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-colors ${
          file && !loading ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        {loading ? (
          <>
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            Uploading…
          </>
        ) : 'Upload Websites'}
      </button>

      {result && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-green-800 mb-1">Upload complete</p>
          <p className="text-sm text-green-700">
            {result.updated} {result.updated === 1 ? 'agency' : 'agencies'} updated.
            {result.not_found > 0 && ` ${result.not_found} not matched.`}
          </p>
          {result.unmatched?.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-green-700 cursor-pointer hover:text-green-900 font-medium">
                Show unmatched agency names ({result.unmatched.length})
              </summary>
              <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                {result.unmatched.map((name, i) => (
                  <li key={i} className="text-xs text-red-600 font-mono">{name}</li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-2">Check spelling — names must match the Greenbook exactly.</p>
            </details>
          )}
          <button onClick={() => setResult(null)} className="mt-3 text-xs text-green-700 hover:underline">Dismiss</button>
        </div>
      )}
    </div>
  );
}

// ─── Pending Links tab ────────────────────────────────────────────────────────

function PendingLinksTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);

  const load = () => {
    setLoading(true);
    getPendingLinks().then(setRequests).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleApprove = async (userId) => {
    await approveLink(userId);
    load();
  };

  const handleDeny = async (userId) => {
    await denyLink(userId);
    load();
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">
        {requests.length === 0 ? 'No pending requests.' : `${requests.length} pending ${requests.length === 1 ? 'request' : 'requests'}`}
      </p>

      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-6 py-12 text-center">
          <p className="text-gray-400 text-sm">No pending facility link requests.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['User', 'Account Type', 'Requested Facility', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requests.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</p>
                    <p className="text-xs text-gray-400">{r.email}</p>
                    <p className="text-xs text-gray-400">{r.organization_name}</p>
                  </td>
                  <td className="px-4 py-3"><Badge type={r.account_type} /></td>
                  <td className="px-4 py-3">
                    {r.pending_facility ? (
                      <>
                        <p className="font-medium text-gray-900">{r.pending_facility.agency_name}</p>
                        <p className="text-xs text-gray-400">
                          {[r.pending_facility.physical_city, r.pending_facility.physical_county].filter(Boolean).join(', ')}
                        </p>
                      </>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => handleApprove(r.id)}
                        className="text-xs text-green-700 hover:text-green-900 font-semibold bg-green-50 hover:bg-green-100 px-2.5 py-1 rounded-lg transition-colors">
                        Approve
                      </button>
                      <button onClick={() => handleDeny(r.id)}
                        className="text-xs text-red-600 hover:text-red-800 font-medium">
                        Deny
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Notes tab ────────────────────────────────────────────────────────────────

function NotesTab() {
  const [notes, setNotes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    getAdminNotes()
      .then(setNotes)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = notes.filter((n) => {
    const matchType = !typeFilter || n.note_type === typeFilter;
    const matchSearch = !search ||
      n.note_text?.toLowerCase().includes(search.toLowerCase()) ||
      n.added_by_name?.toLowerCase().includes(search.toLowerCase()) ||
      n.facilities?.agency_name?.toLowerCase().includes(search.toLowerCase()) ||
      n.org_name?.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const NOTE_TYPE_LABELS = { general: 'General', insurance: 'Insurance' };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search notes, facility, or user…"
          className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <option value="">All types</option>
          <option value="general">General</option>
          <option value="insurance">Insurance</option>
        </select>
        <p className="self-center text-sm text-gray-400">
          {loading ? '…' : `${filtered.length} note${filtered.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">No notes found</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {filtered.map((n) => (
            <div key={n.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-4 mb-1">
                <div>
                  <span className="font-semibold text-sm text-gray-900">
                    {n.facilities?.agency_name || `Facility #${n.facility_id}`}
                  </span>
                  {n.facilities?.physical_county && (
                    <span className="text-xs text-gray-400 ml-2">{n.facilities.physical_county} County</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    n.note_type === 'insurance'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {NOTE_TYPE_LABELS[n.note_type] || n.note_type}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(n.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{n.note_text}</p>
              <p className="text-xs text-gray-400 mt-1">
                {n.added_by_name}
                {n.org_name && <span> · {n.org_name}</span>}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sober Living tab ─────────────────────────────────────────────────────────

const SL_GENDER_OPTIONS = ['', "Men's", "Women's", 'Co-ed'];

function SoberLivingModal({ entry, onSave, onClose }) {
  const blank = { name: '', organization: '', street: '', city: '', state: '', zip: '', county: '', phone: '', website: '', gender: '', total_beds: '', baths: '', cost: '', payment_options: '', accreditation: '', accepts_mat: false, description: '' };
  const [form, setForm]   = useState(entry ? { ...blank, ...entry, total_beds: entry.total_beds || '', baths: entry.baths || '', accepts_mat: !!entry.accepts_mat } : blank);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ ...form, total_beds: form.total_beds !== '' ? Number(form.total_beds) : 0, baths: form.baths !== '' ? Number(form.baths) : 0 });
    } catch (err) {
      setError(err?.response?.data?.error || 'Save failed.');
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">{entry ? 'Edit' : 'Add'} Sober Living Home</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">✕</button>
        </div>
        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
            <input type="text" value={form.name} onChange={set('name')} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Organization</label>
            <input type="text" value={form.organization} onChange={set('organization')} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Street Address</label>
            <input type="text" value={form.street} onChange={set('street')} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
              <input type="text" value={form.city} onChange={set('city')} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
              <input type="text" value={form.state} onChange={set('state')} className={inputCls} placeholder="WA" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">ZIP</label>
              <input type="text" value={form.zip} onChange={set('zip')} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">County</label>
              <select value={form.county} onChange={set('county')} className={`${inputCls} bg-white`}>
                <option value="">— Select —</option>
                {WA_COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
              <input type="text" value={form.phone} onChange={set('phone')} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
              <input type="url" value={form.website} onChange={set('website')} className={inputCls} placeholder="https://..." />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
              <select value={form.gender} onChange={set('gender')} className={`${inputCls} bg-white`}>
                {SL_GENDER_OPTIONS.map((g) => <option key={g} value={g}>{g || '— Not specified —'}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Beds</label>
              <input type="number" min={0} value={form.total_beds} onChange={set('total_beds')} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Baths</label>
              <input type="number" min={0} value={form.baths} onChange={set('baths')} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cost</label>
            <input type="text" value={form.cost} onChange={set('cost')} className={inputCls} placeholder="e.g. $500–$800/mo" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Payment Options</label>
            <input type="text" value={form.payment_options} onChange={set('payment_options')} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Accreditation</label>
            <input type="text" value={form.accreditation} onChange={set('accreditation')} className={inputCls} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.accepts_mat} onChange={set('accepts_mat')} className="w-4 h-4 rounded border-gray-300 text-blue-600" />
            <span className="text-sm text-gray-700">MAT friendly</span>
          </label>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea value={form.description} onChange={set('description')} rows={3} className={`${inputCls} resize-none`} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-5 py-2 text-sm rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SoberLivingTab() {
  const [homes, setHomes]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [editing, setEditing]       = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get('/api/sober-living').then((r) => setHomes(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleAdd = async (data) => {
    const res = await axios.post('/api/sober-living', data);
    setHomes((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
    setShowAdd(false);
  };

  const handleEdit = async (data) => {
    const res = await axios.patch(`/api/sober-living/${editing.id}`, data);
    setHomes((prev) => prev.map((h) => h.id === editing.id ? res.data : h));
    setEditing(null);
  };

  const handleDelete = async (id) => {
    await axios.delete(`/api/sober-living/${id}`);
    setHomes((prev) => prev.filter((h) => h.id !== id));
    setConfirmDelete(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{homes.length} sober living {homes.length === 1 ? 'home' : 'homes'}</p>
        <button onClick={() => setShowAdd(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
          + Add home
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
      ) : homes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-6 py-12 text-center">
          <p className="text-gray-400 text-sm">No sober living homes added yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name', 'County / City', 'Phone', 'Gender', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {homes.map((h) => (
                <tr key={h.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{h.name}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <p>{h.county || '—'}</p>
                    {h.city && <p className="text-xs text-gray-400">{h.city}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{h.phone || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{h.gender || '—'}</td>
                  <td className="px-4 py-3">
                    {confirmDelete === h.id ? (
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleDelete(h.id)} className="text-xs text-red-600 hover:text-red-800 font-medium">Confirm</button>
                        <button onClick={() => setConfirmDelete(null)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <button onClick={() => setEditing(h)} className="text-xs text-blue-600 hover:text-blue-800">Edit</button>
                        <button onClick={() => setConfirmDelete(h.id)} className="text-xs text-gray-400 hover:text-red-600">Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showAdd  && <SoberLivingModal onSave={handleAdd}  onClose={() => setShowAdd(false)} />}
      {editing  && <SoberLivingModal entry={editing} onSave={handleEdit} onClose={() => setEditing(null)} />}

      <hr className="border-gray-200 my-6" />
      <div className="max-w-lg">
        <UploadSection
          title="Import from Spreadsheet"
          description="Expected columns: House Name, Organization, City, State, Zip, Phone (sync key), Beds, Baths, Gender, Cost, Payment Options, Accreditation, Notes. County and website can be added manually after import."
          uploadFn={(fd) => uploadSoberLiving(fd).then((r) => { load(); return r; })}
          passwordRequired={false}
        />
      </div>
    </div>
  );
}

// ─── Main Admin page ──────────────────────────────────────────────────────────

const TABS = ['Overview', 'Users', 'Promo Codes', 'Facilities', 'Sober Living', 'Pending Links', 'Notes', 'Upload'];

export default function Admin() {
  const [activeTab, setActiveTab] = useState('Overview');

  return (
    <div className="min-h-screen bg-gray-50">
      <Header right={<span className="text-blue-200 text-sm">Admin Dashboard</span>} />

      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 -mx-1 overflow-x-auto">
          {TABS.map((tab) => (
            <TabButton key={tab} label={tab} active={activeTab === tab} onClick={() => setActiveTab(tab)} />
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'Overview'       && <OverviewTab />}
        {activeTab === 'Users'          && <UsersTab />}
        {activeTab === 'Promo Codes'    && <PromoTab />}
        {activeTab === 'Facilities'     && <FacilitiesTab />}
        {activeTab === 'Sober Living'   && <SoberLivingTab />}
        {activeTab === 'Pending Links'  && <PendingLinksTab />}
        {activeTab === 'Notes'          && <NotesTab />}
        {activeTab === 'Upload'         && <UploadTab />}
      </div>
    </div>
  );
}
